import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Stack,
  TextField,
  MenuItem,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Switch,
  IconButton,
  Collapse,
  Grid,
  Skeleton,
  Avatar,
  InputAdornment,
  Alert,
  Chip,
  Divider,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SaveIcon from "@mui/icons-material/Save";
import ListAltIcon from "@mui/icons-material/ListAlt";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { api } from "../../utils/api";
import { useAuth } from "../../context/AuthContext";

const SERVICE_TYPES = [
  "Accounting", "Audit", "Book Keeping", "Business Setup", "Certification",
  "Company Compliance", "Corporate Law", "Due Diligence", "Financial Reporting",
  "General", "GST", "Income Tax", "Internal Audit", "Legal Drafting",
  "Licenses & Registrations", "Payroll", "ROC / MCA", "Risk Advisory",
  "Secretarial Services", "Statutory Audit", "TDS", "Taxation", "Valuation",
];

const FREQ_LABEL = {
  one_time: "One Time",
  monthly: "Monthly",
  quarterly: "Quarterly",
  half_yearly: "Half Yearly",
  annual: "Annual",
};

const MONTH_SHORT = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun",
                     "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Smart cycle description
const formatSchedule = (s) => {
  const freq = s.frequency;
  const d = s.due_day;
  const m = s.due_month;
  if (!freq || freq === "one_time") return "—";
  if (freq === "monthly") return d ? `Day ${d} every month` : "—";
  if (!m || !d) return "—";
  const mi = Number(m);
  if (freq === "quarterly") {
    const months = [mi, ((mi + 2) % 12) + 1, ((mi + 5) % 12) + 1, ((mi + 8) % 12) + 1];
    return `${months.map((x) => MONTH_SHORT[x]).join("/")} · Day ${d}`;
  }
  if (freq === "half_yearly") {
    const second = ((mi + 5) % 12) + 1;
    return `${MONTH_SHORT[mi]}/${MONTH_SHORT[second]} · Day ${d}`;
  }
  if (freq === "annual") return `${MONTH_SHORT[mi]} ${d} yearly`;
  return "—";
};

const normalize = (str) => (str || "").toLowerCase().replace(/[\s_-]+/g, "");

export default function ClientServices() {
  const { id } = useParams();
  const { can } = useAuth();
  const canManage = can("clients.services");
  const [services, setServices] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [filter, setFilter] = useState({ search: "", type: "", frequency: "", status: "" });

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/clients/services_status.php?id=${id}`);
      const rows = (res.services || []).map((s) => ({
        ...s,
        expand: false,
      }));
      setServices(rows);
      setManagers(res.managers || []);
    } catch (e) {
      setMsg({ type: "error", text: e.message || "Failed to load services" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line
  }, [id]);

  const updateField = (sid, key, value) =>
    setServices((old) =>
      old.map((s) => (s.service_id === sid ? { ...s, [key]: value } : s))
    );

  const toggleActive = (sid) =>
    setServices((old) =>
      old.map((s) =>
        s.service_id === sid ? { ...s, is_active: s.is_active ? 0 : 1 } : s
      )
    );

  const toggleExpand = (sid) =>
    setServices((old) =>
      old.map((s) =>
        s.service_id === sid ? { ...s, expand: !s.expand } : s
      )
    );

  const saveChanges = async () => {
    setSaving(true);
    setMsg(null);
    try {
      await api.post("/clients/checklist_update.php", {
        client_id: id,
        services: services.map((s) => ({
          service_id: s.service_id,
          is_active: s.is_active,
          price: s.price,
          custom_sla_days: s.custom_sla_days,
          custom_manager_id: s.custom_manager_id,
          start_date: s.start_date || null,
          end_date: s.end_date || null,
          notes: s.notes || null,
        })),
      });
      setMsg({ type: "success", text: "Subscriptions saved successfully" });
      loadData();
    } catch (err) {
      setMsg({ type: "error", text: err.message || "Save failed" });
    } finally {
      setSaving(false);
    }
  };

  const filtered = services.filter((s) => {
    const matchSearch = (s.name || "")
      .toLowerCase()
      .includes(filter.search.toLowerCase());
    const matchFreq = filter.frequency
      ? normalize(s.frequency) === normalize(filter.frequency)
      : true;
    const matchType = filter.type
      ? normalize(s.service_type).includes(normalize(filter.type))
      : true;
    const matchStatus =
      filter.status === ""
        ? true
        : filter.status === "active"
        ? s.is_active === 1
        : s.is_active === 0;
    return matchSearch && matchFreq && matchType && matchStatus;
  });

  const activeCount = services.filter((s) => s.is_active === 1).length;
  const oneTimeCount = services.filter(
    (s) => s.is_active === 1 && s.frequency === "one_time"
  ).length;
  const repeatedCount = services.filter(
    (s) => s.is_active === 1 && s.frequency && s.frequency !== "one_time"
  ).length;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Avatar sx={{ bgcolor: "info.main", width: 48, height: 48 }}>
          <ListAltIcon />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>
            Client Subscriptions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Assign services, set custom pricing, SLA and scheduling
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Chip label={`Active: ${activeCount}`} color="primary" size="small" />
          <Chip label={`Recurring: ${repeatedCount}`} color="info" size="small" />
          <Chip label={`One-time: ${oneTimeCount}`} color="secondary" size="small" />
        </Stack>
        {canManage && (
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={saveChanges}
            disabled={saving}
          >
            {saving ? "Saving…" : "Save Changes"}
          </Button>
        )}
      </Stack>

      {msg && (
        <Alert severity={msg.type} sx={{ mb: 2 }} onClose={() => setMsg(null)}>
          {msg.text}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
          <TextField
            size="small"
            placeholder="Search service"
            value={filter.search}
            onChange={(e) => setFilter({ ...filter, search: e.target.value })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 200 }}
          />
          <TextField
            size="small"
            select
            label="Category"
            value={filter.type}
            onChange={(e) => setFilter({ ...filter, type: e.target.value })}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All</MenuItem>
            {SERVICE_TYPES.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            select
            label="Frequency"
            value={filter.frequency}
            onChange={(e) => setFilter({ ...filter, frequency: e.target.value })}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="one_time">One Time</MenuItem>
            <MenuItem value="monthly">Monthly</MenuItem>
            <MenuItem value="quarterly">Quarterly</MenuItem>
            <MenuItem value="half_yearly">Half Yearly</MenuItem>
            <MenuItem value="annual">Annual</MenuItem>
          </TextField>
          <TextField
            size="small"
            select
            label="Status"
            value={filter.status}
            onChange={(e) => setFilter({ ...filter, status: e.target.value })}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="active">Subscribed</MenuItem>
            <MenuItem value="inactive">Not Subscribed</MenuItem>
          </TextField>
        </Stack>
      </Paper>

      <Paper sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Service</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Frequency</TableCell>
              <TableCell>Schedule</TableCell>
              <TableCell>Price</TableCell>
              <TableCell align="center">Subscribed</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading &&
              [...Array(5)].map((_, i) => (
                <TableRow key={`sk-${i}`}>
                  <TableCell colSpan={8}>
                    <Skeleton height={36} />
                  </TableCell>
                </TableRow>
              ))}

            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    No services match your filters
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {!loading &&
              filtered.map((s, index) => (
                <React.Fragment key={s.service_id}>
                  <TableRow hover sx={{ bgcolor: s.is_active ? "action.hover" : "inherit" }}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 600, maxWidth: 320 }}>
                      {s.name}
                      {s.custom_manager_name && (
                        <Typography variant="caption" color="primary" display="block">
                          Manager: {s.custom_manager_name}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{s.service_type}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={FREQ_LABEL[s.frequency] || s.frequency || "—"}
                        color={s.frequency === "one_time" ? "secondary" : "info"}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{formatSchedule(s)}</Typography>
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={s.price ?? ""}
                        onChange={(e) =>
                          updateField(s.service_id, "price", e.target.value)
                        }
                        disabled={!canManage}
                        sx={{ width: 110 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">₹</InputAdornment>
                          ),
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        size="small"
                        checked={s.is_active === 1}
                        onChange={() => toggleActive(s.service_id)}
                        disabled={!canManage}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="More options">
                        <IconButton
                          size="small"
                          onClick={() => toggleExpand(s.service_id)}
                        >
                          {s.expand ? (
                            <KeyboardArrowUpIcon />
                          ) : (
                            <KeyboardArrowDownIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={8} sx={{ py: 0, border: 0 }}>
                      <Collapse in={s.expand} unmountOnExit>
                        <Box sx={{ p: 2, bgcolor: "grey.50" }}>
                          {s.frequency && s.frequency !== "one_time" && (
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 1,
                                mb: 2,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                bgcolor: "info.50",
                                borderStyle: "dashed",
                              }}
                            >
                              <EventRepeatIcon color="info" fontSize="small" />
                              <Typography variant="caption">
                                Recurring: {formatSchedule(s)} — jobs will auto-generate based on this schedule
                              </Typography>
                            </Paper>
                          )}

                          {s.frequency === "one_time" && (
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 1,
                                mb: 2,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                bgcolor: "grey.100",
                                borderStyle: "dashed",
                              }}
                            >
                              <InfoOutlinedIcon color="action" fontSize="small" />
                              <Typography variant="caption">
                                One-time service — job will be created manually when needed
                              </Typography>
                            </Paper>
                          )}

                          <Grid container spacing={1.5}>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                size="small"
                                fullWidth
                                type="number"
                                label={`SLA Days (default ${s.default_sla_days ?? "—"})`}
                                value={s.custom_sla_days ?? ""}
                                onChange={(e) =>
                                  updateField(
                                    s.service_id,
                                    "custom_sla_days",
                                    e.target.value
                                  )
                                }
                                placeholder="Use default"
                              />
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                size="small"
                                select
                                fullWidth
                                label={`Manager (default ${s.default_manager_name || "—"})`}
                                value={s.custom_manager_id ?? ""}
                                onChange={(e) =>
                                  updateField(
                                    s.service_id,
                                    "custom_manager_id",
                                    e.target.value
                                  )
                                }
                              >
                                <MenuItem value="">Use default</MenuItem>
                                {managers.map((u) => (
                                  <MenuItem key={u.id} value={u.id}>
                                    {u.name}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                size="small"
                                fullWidth
                                type="date"
                                label="Start Date"
                                value={s.start_date || ""}
                                onChange={(e) =>
                                  updateField(
                                    s.service_id,
                                    "start_date",
                                    e.target.value
                                  )
                                }
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                size="small"
                                fullWidth
                                type="date"
                                label="End Date"
                                value={s.end_date || ""}
                                onChange={(e) =>
                                  updateField(
                                    s.service_id,
                                    "end_date",
                                    e.target.value
                                  )
                                }
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <TextField
                                size="small"
                                fullWidth
                                label="Notes"
                                value={s.notes || ""}
                                onChange={(e) =>
                                  updateField(s.service_id, "notes", e.target.value)
                                }
                                placeholder="Internal notes about this subscription"
                              />
                            </Grid>

                            {s.last_cycle_generated && (
                              <Grid item xs={12}>
                                <Divider sx={{ my: 0.5 }} />
                                <Typography variant="caption" color="text.secondary">
                                  Last auto-generated cycle:{" "}
                                  <b>{s.last_cycle_generated}</b>
                                </Typography>
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
